<template>
    <v-container>
        <template v-if="user?.is_subscribed || user?.is_admin || $page.props.free_use"> </template>
        <v-row class="mt-2">
            <v-col cols="12" sm="12" md="4" lg="4" class="d-flex align-start flex-column">
                <scorsync-avatar :person="club" size="lg" class="mx-auto" />
            </v-col>

            <v-col cols="12" sm="12" md="8" lg="8" class="d-flex justify-center">
                <v-container>
                    <v-row>
                        <v-col>
                            <h3
                                class="text-h3"
                                :class="{ 'text-center': $vuetify.breakpoint.xs || $vuetify.breakpoint.sm }"
                            >
                                {{ club.name }}
                            </h3>
                            <div
                                v-if="club.country"
                                class="text-h3 mt-2"
                                :class="{ 'text-center': $vuetify.breakpoint.xs || $vuetify.breakpoint.sm }"
                            >
                                <country-flag :country="club.country" />
                            </div>
                            <h2
                                class="text-h5 mt-2"
                                :class="{ 'text-center': $vuetify.breakpoint.xs || $vuetify.breakpoint.sm }"
                            >
                                {{ club.athletes.length }}
                                <span v-if="club.athletes.length > 1">athletes</span>
                                <span v-else>athlete</span>
                            </h2>

                            <div
                                class="mt-4 d-flex justify-center justify-xs-center justify-sm-center justify-md-start justify-lg-start"
                            >
                                <s-follow :entity="club" />
                            </div>

                            <div v-if="club.jobs?.length > 0" class="mt-4">
                                <h5 class="text-h5">Hiring</h5>

                                <v-row>
                                    <v-col v-for="job in club.jobs" :key="job.id">
                                        <v-card :href="$route('jobs.show', job)">
                                            <v-card-title class="d-flex justify-space-between">
                                                <div>{{ job.title }}</div>
                                                <div><v-icon>mdi-arrow-right-circle-outline</v-icon></div>
                                            </v-card-title>

                                            <v-card-text>
                                                {{ job.short_description }}
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>

                                <v-row v-if="totalJobs > 4" class="mt-0">
                                    <v-col cols="12">
                                        <v-btn :href="$route('jobs.index')" text block> View All Jobs... </v-btn>
                                    </v-col>
                                </v-row>
                            </div>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-tabs v-model="tab" class="mt-4" show-arrows>
                    <v-tab href="#tab-athletes"> Athletes</v-tab>
                    <v-tab href="#tab-results"> Results</v-tab>
                    <v-tab href="#tab-mobility"> Mobility</v-tab>
                    <v-tab href="#tab-qualifiers"> Qualifiers</v-tab>
                    <v-tab v-if="false" href="#tab-photos"> Photos</v-tab>
                    <v-tab href="#tab-compare-scores">Compare Scores</v-tab>
                </v-tabs>

                <v-tabs-items v-model="tab">
                    <v-tab-item value="tab-athletes">
                        <athletes-table :club="club" :paginate="false" />
                    </v-tab-item>

                    <v-tab-item value="tab-results">
                        <club-results :club-id="club.id" />
                    </v-tab-item>

                    <v-tab-item value="tab-mobility">
                        <template v-if="user?.is_subscribed || user?.is_admin || $page.props.free_use">
                            <club-mobilities :club-id="club.id" />
                        </template>
                        <template v-else>
                            <subscription-required />
                        </template>
                    </v-tab-item>

                    <v-tab-item value="tab-qualifiers">
                        <template v-if="user?.is_subscribed || user?.is_admin || $page.props.free_use">
                            <club-qualifiers v-if="qualifiersLoaded" :qualifiers="qualifiers" />

                            <template v-else>
                                <v-skeleton-loader
                                    type="list-item-avatar, list-item-avatar, list-item-avatar, list-item-avatar, list-item-avatar"
                                />
                            </template>
                        </template>
                        <template v-else>
                            <subscription-required />
                        </template>
                    </v-tab-item>

                    <v-tab-item v-if="false" value="tab-photos">
                        <club-athlete-photos :club="club" />
                    </v-tab-item>

                    <v-tab-item value="tab-compare-scores">
                        <club-score-comparison :club-id="club.id" :comparison="comparison" />
                    </v-tab-item>
                </v-tabs-items>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import Club from '@/Models/Club.js';
import { ordinal } from '@/helpers.js';
import ScorsyncAvatar from '@/Components/ScorsyncAvatar.vue';
import AthletesTable from '@/Components/AthletesTable.vue';
import ClubResults from '@/Components/ClubResults.vue';
import ClubQualifiers from '@/Components/ClubQualifiers.vue';
import SFollow from '@/Components/SFollow.vue';
import User from '@/Models/User.js';
import PageMixin from '@/Pages/Page.mixin.js';
import ClubAthletePhotos from '@/Components/Club/ClubAthletePhotos.vue';
import SubscriptionRequired from '@/Components/SubscriptionRequired.vue';
import CountryFlag from '@/Components/CountryFlag.vue';
import ClubMobilities from '@/Components/ClubMobilities.vue';
import ClubScoreComparison from '@/Components/ClubScoreComparison.vue';
import Job from '@/Models/Job.js';

export default {
    name: 'Club',

    components: {
        ClubScoreComparison,
        ClubMobilities,
        CountryFlag,
        SubscriptionRequired,
        ClubAthletePhotos,
        SFollow,
        ClubQualifiers,
        ClubResults,
        AthletesTable,
        ScorsyncAvatar,
    },

    mixins: [PageMixin],

    props: {
        clubId: {
            type: Number,
            required: true,
        },

        comparison: {
            type: Array,
            required: false,
            default: () => [],
        },
    },

    data: () => ({
        tab: 'tab-athletes',
        filterEvent: null,
        qualifiersLoaded: false,
        qualifiers: null,
        mobilitiesLoaded: false,
        mobilities: null,

        headers: [
            {
                text: 'Year',
                sortable: true,
                value: 'meet.year',
            },
            {
                text: 'Meet',
                sortable: true,
                value: 'meet.title',
            },
            {
                text: 'Event',
                sortable: true,
                value: 'event',
            },
            {
                text: 'Level',
                sortable: true,
                value: 'level',
            },
            {
                text: 'Score',
                sortable: true,
                value: 'total_score',
            },
            {
                text: 'Place',
                sortable: true,
                value: 'overall_placement',
            },
            {
                text: '',
                sortable: false,
                value: 'view',
            },
        ],

        events: [
            {
                text: 'TRA',
                value: 'TI',
            },
            {
                text: 'DMT',
                value: 'DM',
            },
            {
                text: 'TUM',
                value: 'TU',
            },
            {
                text: 'SYN',
                value: 'TS',
            },
        ],
    }),

    computed: {
        club() {
            return Club.query()
                .with(['athletes', 'country'])
                .with('jobs', (query) => query.orderBy('created_at', 'desc').limit(4))
                .find(this.clubId);
        },

        totalJobs() {
            return Job.query().where('club_id', this.clubId).count();
        },

        user() {
            if (!this.$page.props.auth.user) {
                return null;
            }

            return User.query().with('affiliations').find(this.$page.props.auth.user.id);
        },

        bookmarklet() {
            return `(function(e,a){if(window.location.host!=='usagym.org'||window.location.pathname!=='/app/AddMeetRegistration.html'){alert('You must be on a meet registration page to use this script.')}var n={1:'73',2:'74',3:'75',4:'76',5:'77',6:'78',7:'79',8:'80',9:'81',10:'82','YE 11-12':'134','YE 13-14':'131',JR:'99',Open:'101',OE:'101',IE:'223',SR:'100'};var r;fetch(e+'/api/usagym-registration?club_id='+a,{headers:{'Content-Type':'application/json'}}).then(e=>{if(!e.ok){r=e;throw Error('There was an issue reaching out to Scorsync.')}return e}).then(e=>e.json()).then(e=>{var a=e.data.filter(e=>e.usag_number!==null);var r=a.map(e=>String(e.usag_number));$('#participantsWomen tr, #participantsMen tr').each((e,a)=>{var t=r.includes($(a).find('td:nth-child(4)').text());$(a).find('td:nth-child(1) input').prop('checked',t)});a.forEach(e=>{var a=n[e.tra_level];var t=n[e.dmt_level];var r=n[e.tum_level];if(a){$('select[name=\\'apparatus['+e.usag_number+'][5]\\']').val(n[e.tra_level])}if(t){$('select[name=\\'apparatus['+e.usag_number+'][7]\\']').val(n[e.dmt_level])}if(r){$('select[name=\\'apparatus['+e.usag_number+'][6]\\']').val(n[e.tum_level])}})}).catch(t=>{r.json().then(e=>{var a=e?e.message:t;console.error(e);alert('There was an error retrieving the athlete information from Scorsync. Please try again later. Error: '+a)})})})('${window.location.origin}','${this.clubId}');`;
        },
    },

    watch: {
        tab(tab) {
            window.location.hash = tab;

            if (tab === 'tab-qualifiers' && !this.qualifiersLoaded) {
                this.loadQualifiers();
            }
        },
    },

    async created() {
        if (this.$page.props.auth.user) {
            await User.insert({ data: this.$page.props.auth.user });
        }

        this.setTabFromUrl();

        window.addEventListener('hashchange', this.setTabFromUrl);
    },

    beforeDestroy() {
        window.removeEventListener('hashchange', this.setTabFromUrl);
    },

    methods: {
        ordinal,

        href(result) {
            return `/result-categories/${result.category.id}`;
        },

        async loadQualifiers() {
            this.qualifiersLoaded = false;

            const { data } = await axios.get(`/api/clubs/${this.clubId}/qualifiers`);

            this.qualifiers = data.data;

            this.qualifiersLoaded = true;
        },

        setTabFromUrl() {
            const urlHash = window.location.hash;

            if (urlHash) {
                const tabFromUrl = urlHash.replace('#', '');

                if (tabFromUrl) {
                    this.tab = tabFromUrl;
                }
            }
        },
    },
};
</script>
